// FFL Year-1 commission rates by carrier × product × contract level.
//
// Source: Family First Life Commission Charts (rev 12/21/2023)
//   fflcalc.netlify.app
// INTERNAL USE ONLY — rates may shift. Verify against your official
// FFL comp guide before relying on these for a real payout.
//
// Two structures coexist:
//   • FFL_LEVELS — numeric contract levels (65-145), used by all life
//     carriers below.
//   • FFL_ANNUITY_LEVELS — named producer tiers (Green→Red), used by
//     the annuity carriers.
//
// All rates are stored as decimals (1.44 means 144%). Helper
// `calcCommission` returns the full payout breakdown for a given
// (carrier, product, level, annualPremium).

// ── Numeric FFL levels, descending (highest first for selector UX) ──
export const FFL_LEVELS = [145, 140, 135, 130, 125, 120, 115, 110, 105, 100, 95, 90, 85, 80, 75, 70, 65];

// ── Named annuity producer levels ────────────────────────────────────
export const FFL_ANNUITY_LEVELS = [
  { key: 'green',    label: 'Green — New Agent' },
  { key: 'yellow',   label: 'Yellow — 100k' },
  { key: 'blue',     label: 'Blue — 500k' },
  { key: 'silver',   label: 'Silver — 1M' },
  { key: 'gold',     label: 'Gold — 2M (FFL 140/145)' },
  { key: 'platinum', label: 'Platinum — 5M (30M agency)' },
  { key: 'black',    label: 'Black — 10M (60M agency)' },
  { key: 'royal',    label: 'Royal — 120M agency' },
  { key: 'red',      label: 'Red — 175M agency' },
];

// ────────────────────────────────────────────────────────────────────
// Life carriers — keyed by display name. Each product holds a
// { level: rate } map. Levels not present in the table (e.g. NLG at
// FFL 145, Royal Neighbors at FFL 145) are simply omitted.
// ────────────────────────────────────────────────────────────────────

const AETNA = {
  type: 'life',
  productLine: 'Final Expense',
  products: {
    'ACC Final Expense':  { 145: 1.44, 140: 1.44, 135: 1.37,  130: 1.30, 125: 1.25,  120: 1.20, 115: 1.15, 110: 1.075, 105: 1.00, 100: 0.925, 95: 0.85, 90: 0.775, 85: 0.70, 80: 0.70, 75: 0.70, 70: 0.70, 65: 0.70 },
    'CLI Protection FE':  { 145: 0.99, 140: 0.99, 135: 0.975, 130: 0.95, 125: 0.935, 120: 0.90, 115: 0.89, 110: 0.83,  105: 0.80, 100: 0.73,  95: 0.70, 90: 0.68,  85: 0.65, 80: 0.65, 75: 0.65, 70: 0.65, 65: 0.65 },
  },
};

const AFLAC = {
  type: 'life',
  productLine: 'Final Expense',
  products: {
    'Final EX': { 140: 1.23, 135: 1.18, 130: 1.13, 125: 1.08, 120: 1.00, 115: 0.93, 110: 0.85, 105: 0.78, 100: 0.70, 95: 0.70, 90: 0.70, 85: 0.63, 80: 0.63, 75: 0.63, 70: 0.63, 65: 0.63 },
  },
};

const AMERICO = {
  type: 'life',
  productLine: 'Final Expense / Term / IUL',
  products: {
    'Eagle Select FE':        { 145: 1.25, 140: 1.25, 135: 1.20, 130: 1.15, 125: 1.10, 120: 1.05, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.85, 95: 0.80, 90: 0.75, 85: 0.70, 80: 0.65, 75: 0.60, 70: 0.55, 65: 0.50 },
    'Term 100':               { 145: 1.30, 140: 1.25, 135: 1.20, 130: 1.15, 125: 1.10, 120: 1.05, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.85, 95: 0.80, 90: 0.75, 85: 0.70, 80: 0.65, 75: 0.60, 70: 0.55, 65: 0.50 },
    'Term 125':               { 145: 1.45, 140: 1.40, 135: 1.35, 130: 1.30, 125: 1.25, 120: 1.20, 115: 1.15, 110: 1.10, 105: 1.05, 100: 1.00, 95: 0.95, 90: 0.90, 85: 0.85, 80: 0.80, 75: 0.75, 70: 0.70, 65: 0.65 },
    'Instant Decision IUL':   { 145: 1.30, 140: 1.25, 135: 1.20, 130: 1.15, 125: 1.10, 120: 1.05, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.85, 95: 0.80, 90: 0.75, 85: 0.70, 80: 0.65, 75: 0.60, 70: 0.55, 65: 0.50 },
  },
};

const AMERICAN_AMICABLE = {
  type: 'life',
  productLine: 'Final Expense / Term / IUL',
  products: {
    'Senior/Family Choice':   { 145: 1.25, 140: 1.25, 135: 1.20, 130: 1.15, 125: 1.10, 120: 1.05, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.85, 95: 0.80, 90: 0.75, 85: 0.70, 80: 0.65, 75: 0.60, 70: 0.55, 65: 0.50 },
    'EZ Term':                { 145: 1.05, 140: 1.05, 135: 1.00, 130: 0.95, 125: 0.90, 120: 0.85, 115: 0.80, 110: 0.75, 105: 0.70, 100: 0.65, 95: 0.60, 90: 0.55, 85: 0.50, 80: 0.45, 75: 0.40, 70: 0.35, 65: 0.30 },
    'Secure Life Plus':       { 145: 1.40, 140: 1.40, 135: 1.35, 130: 1.30, 125: 1.25, 120: 1.20, 115: 1.15, 110: 1.10, 105: 1.05, 100: 1.00, 95: 0.95, 90: 0.90, 85: 0.85, 80: 0.80, 75: 0.75, 70: 0.70, 65: 0.65 },
    'Home Protector':         { 145: 1.45, 140: 1.40, 135: 1.35, 130: 1.30, 125: 1.25, 120: 1.20, 115: 1.15, 110: 1.10, 105: 1.05, 100: 1.00, 95: 0.95, 90: 0.90, 85: 0.85, 80: 0.80, 75: 0.75, 70: 0.70, 65: 0.65 },
    'OBA':                    { 145: 1.00, 140: 1.00, 135: 0.95, 130: 0.90, 125: 0.85, 120: 0.80, 115: 0.75, 110: 0.70, 105: 0.65, 100: 0.60, 95: 0.55, 90: 0.50, 85: 0.45, 80: 0.40, 75: 0.35, 70: 0.30, 65: 0.30 },
    'Term Made Simple':       { 145: 1.30, 140: 1.30, 135: 1.25, 130: 1.20, 125: 1.15, 120: 1.10, 115: 1.05, 110: 1.00, 105: 0.95, 100: 0.90, 95: 0.85, 90: 0.80, 85: 0.75, 80: 0.70, 75: 0.65, 70: 0.60, 65: 0.55 },
    'Family Protector':       { 145: 1.30, 140: 1.30, 135: 1.25, 130: 1.20, 125: 1.15, 120: 1.10, 115: 1.05, 110: 1.00, 105: 0.95, 100: 0.90, 95: 0.85, 90: 0.80, 85: 0.75, 80: 0.70, 75: 0.65, 70: 0.60, 65: 0.55 },
    'Intelligent Choice IUL': { 145: 1.25, 140: 1.25, 135: 1.20, 130: 1.15, 125: 1.10, 120: 1.05, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.85, 95: 0.80, 90: 0.75, 85: 0.70, 80: 0.65, 75: 0.60, 70: 0.55, 65: 0.50 },
  },
};

const COREBRIDGE = {
  type: 'life',
  productLine: 'Final Expense',
  products: {
    'GIWL': { 145: 0.80,  140: 0.80,  135: 0.775, 130: 0.75, 125: 0.725, 120: 0.70, 115: 0.675, 110: 0.65, 105: 0.625, 100: 0.60, 95: 0.575, 90: 0.575, 85: 0.55, 80: 0.55, 75: 0.55, 70: 0.55, 65: 0.55 },
    'SIWL': { 145: 1.32,  140: 1.32,  135: 1.27,  130: 1.22, 125: 1.17,  120: 1.12, 115: 1.07,  110: 1.02, 105: 0.97,  100: 0.92, 95: 0.87,  90: 0.82,  85: 0.77, 80: 0.72, 75: 0.67, 70: 0.62, 65: 0.57 },
  },
};

const ETHOS = {
  type: 'life',
  productLine: 'Multi-product',
  products: {
    'LGA Prime':        { 145: 1.20,  140: 1.175, 135: 1.15,  130: 1.125, 125: 1.10,  120: 1.075, 115: 1.05,  110: 1.025, 105: 1.00,  100: 0.975, 95: 0.95,  90: 0.925, 85: 0.90, 80: 0.875, 75: 0.85, 70: 0.825, 65: 0.80 },
    'TruStage TAWL':    { 145: 1.20,  140: 1.20,  135: 1.15,  130: 1.10,  125: 1.05,  120: 1.00,  115: 0.95,  110: 0.90,  105: 0.85,  100: 0.825, 95: 0.80,  90: 0.775, 85: 0.75, 80: 0.725, 75: 0.65, 70: 0.60,  65: 0.55 },
    'TruStage SITL':    { 145: 1.20,  140: 1.15,  135: 1.10,  130: 1.05,  125: 1.00,  120: 0.95,  115: 0.90,  110: 0.85,  105: 0.80,  100: 0.75,  95: 0.70,  90: 0.65,  85: 0.60, 80: 0.55,  75: 0.50, 70: 0.50,  65: 0.50 },
    'TruStage GAWL':    { 145: 0.30,  140: 0.275, 135: 0.25,  130: 0.225, 125: 0.20,  120: 0.175, 115: 0.15,  110: 0.125, 105: 0.10,  100: 0.075, 95: 0.05,  90: 0.025, 85: 0.025, 80: 0.025, 75: 0.025, 70: 0.025, 65: 0.025 },
    'Ethos IUL':        { 145: 1.25,  140: 1.32,  135: 1.15,  130: 1.10,  125: 1.05,  120: 1.00,  115: 0.95,  110: 0.90,  105: 0.85,  100: 0.80,  95: 0.75,  90: 0.70,  85: 0.65, 80: 0.60,  75: 0.55, 70: 0.50,  65: 0.45 },
    'Ameritas SI Term': { 145: 1.20,  140: 1.175, 135: 1.15,  130: 1.125, 125: 1.10,  120: 1.075, 115: 1.05,  110: 1.025, 105: 1.00,  100: 0.975, 95: 0.95,  90: 0.925, 85: 0.90, 80: 0.875, 75: 0.85, 70: 0.825, 65: 0.80 },
    'Accidental Death': { 145: 0.85,  140: 0.85,  135: 0.80,  130: 0.75,  125: 0.70,  120: 0.65,  115: 0.60,  110: 0.55,  105: 0.50,  100: 0.45,  95: 0.40,  90: 0.35,  85: 0.30, 80: 0.25,  75: 0.25, 70: 0.25,  65: 0.25 },
  },
};

const FORESTERS = {
  type: 'life',
  productLine: 'Final Expense',
  products: {
    'Strong Foundation': { 145: 1.30, 140: 1.30, 135: 1.25, 130: 1.20, 125: 1.15, 120: 1.10, 115: 1.05, 110: 1.05, 105: 1.00, 100: 0.95, 95: 0.90, 90: 0.85, 85: 0.80, 80: 0.75, 75: 0.70, 70: 0.65, 65: 0.60 },
    'Planright':         { 145: 1.20, 140: 1.20, 135: 1.15, 130: 1.10, 125: 1.05, 120: 1.00, 115: 0.95, 110: 0.90, 105: 0.85, 100: 0.80, 95: 0.75, 90: 0.70, 85: 0.65, 80: 0.60, 75: 0.60, 70: 0.60, 65: 0.60 },
  },
};

const INSTABRAIN = {
  type: 'life',
  productLine: 'Term / Whole Life',
  products: {
    'Life Term 20yr':       { 145: 1.30,  140: 1.25,  135: 1.20,  130: 1.15,  125: 1.10,  120: 1.05,  115: 1.00,  110: 0.95, 105: 0.90,  100: 0.85, 95: 0.80,  90: 0.75, 85: 0.70,  80: 0.65, 75: 0.60,  70: 0.55, 65: 0.55 },
    'Final Expense WL':     { 145: 1.275, 140: 1.25,  135: 1.225, 130: 1.20,  125: 1.175, 120: 1.15,  115: 1.125, 110: 1.10, 105: 1.075, 100: 1.05, 95: 1.025, 90: 1.00, 85: 0.975, 80: 0.95, 75: 0.925, 70: 0.90, 65: 0.90 },
    'Guaranteed Issue WL':  { 145: 0.725, 140: 0.70,  135: 0.675, 130: 0.65,  125: 0.625, 120: 0.60,  115: 0.575, 110: 0.55, 105: 0.525, 100: 0.50, 95: 0.475, 90: 0.45, 85: 0.425, 80: 0.40, 75: 0.375, 70: 0.35, 65: 0.35 },
    'Senior Life Term/WL':  { 145: 0.90,  140: 0.85,  135: 0.80,  130: 0.75,  125: 0.70,  120: 0.65,  115: 0.60,  110: 0.55, 105: 0.50,  100: 0.45, 95: 0.40,  90: 0.35, 85: 0.30,  80: 0.25, 75: 0.20,  70: 0.15, 65: 0.15 },
  },
};

const NLG_LIFE = {
  type: 'life',
  productLine: 'IUL / Universal Life',
  products: {
    'Universal Life': { 140: 1.10, 135: 1.05, 130: 1.00, 125: 0.95, 120: 0.90, 115: 0.85, 110: 0.80, 105: 0.75, 100: 0.70, 95: 0.65, 90: 0.60, 85: 0.55, 80: 0.50, 75: 0.45, 70: 0.40, 65: 0.40 },
  },
};

const MUTUAL_OF_OMAHA = {
  type: 'life',
  productLine: 'Multi-product (FE / Term / IUL)',
  products: {
    'Term Life Express':  { 145: 1.45, 140: 1.40, 135: 1.35, 130: 1.30, 125: 1.25, 120: 1.20, 115: 1.15, 110: 1.10, 105: 1.05, 100: 1.00, 95: 0.95, 90: 0.90, 85: 0.85, 80: 0.80, 75: 0.75, 70: 0.75, 65: 0.65 },
    'Final Expense':      { 145: 1.25, 140: 1.25, 135: 1.20, 130: 1.15, 125: 1.10, 120: 1.05, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.86, 95: 0.82, 90: 0.78, 85: 0.74, 80: 0.70, 75: 0.65, 70: 0.61, 65: 0.57 },
    'IUL Express':        { 145: 1.30, 140: 1.30, 135: 1.25, 130: 1.20, 125: 1.15, 120: 1.10, 115: 1.05, 110: 1.00, 105: 0.95, 100: 0.90, 95: 0.85, 90: 0.80, 85: 0.75, 80: 0.70, 75: 0.65, 70: 0.60, 65: 0.55 },
    "Children's WL":      { 145: 1.00, 140: 1.00, 135: 0.97, 130: 0.95, 125: 0.92, 120: 0.90, 115: 0.85, 110: 0.80, 105: 0.75, 100: 0.70, 95: 0.65, 90: 0.60, 85: 0.55, 80: 0.50, 75: 0.45, 70: 0.40, 65: 0.35 },
    'Universal Life':     { 145: 1.25, 140: 1.25, 135: 1.20, 130: 1.15, 125: 1.10, 120: 1.05, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.85, 95: 0.80, 90: 0.75, 85: 0.70, 80: 0.65, 75: 0.60, 70: 0.55, 65: 0.50 },
    'Accum UL Answers':   { 145: 0.95, 140: 0.95, 135: 0.90, 130: 0.85, 125: 0.80, 120: 0.75, 115: 0.70, 110: 0.65, 105: 0.60, 100: 0.55, 95: 0.50, 90: 0.45, 85: 0.40, 80: 0.35, 75: 0.30, 70: 0.25, 65: 0.20 },
    'Term Life Answers':  { 145: 1.10, 140: 1.10, 135: 1.05, 130: 1.00, 125: 0.95, 120: 0.90, 115: 0.85, 110: 0.80, 105: 0.75, 100: 0.70, 95: 0.65, 90: 0.60, 85: 0.55, 80: 0.50, 75: 0.45, 70: 0.40, 65: 0.35 },
    'Accidental Death':   { 145: 1.30, 140: 1.30, 135: 1.25, 130: 1.20, 125: 1.15, 120: 1.10, 115: 1.05, 110: 1.00, 105: 0.95, 100: 0.90, 95: 0.85, 90: 0.80, 85: 0.75, 80: 0.70, 75: 0.65, 70: 0.60, 65: 0.55 },
  },
};

const ROYAL_NEIGHBORS = {
  type: 'life',
  productLine: 'Term / IUL / Whole Life',
  products: {
    'Term':              { 140: 1.20, 135: 1.15, 130: 1.10, 125: 1.00, 120: 1.00, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.85, 95: 0.80, 90: 0.75, 85: 0.50, 80: 0.50, 75: 0.50, 70: 0.50, 65: 0.50 },
    'Royal Legacy SPWL': { 140: 0.16, 135: 0.15, 130: 0.14, 125: 0.13, 120: 0.13, 115: 0.13, 110: 0.13, 105: 0.12, 100: 0.11, 95: 0.10, 90: 0.09, 85: 0.07, 80: 0.07, 75: 0.07, 70: 0.07, 65: 0.07 },
    'Secure Life IUL':   { 140: 1.25, 135: 1.20, 130: 1.12, 125: 1.05, 120: 1.05, 115: 1.05, 110: 1.00, 105: 0.95, 100: 0.90, 95: 0.85, 90: 0.80, 85: 0.50, 80: 0.50, 75: 0.50, 70: 0.50, 65: 0.50 },
    'SI Whole Life':     { 140: 1.25, 135: 1.20, 130: 1.10, 125: 1.00, 120: 1.00, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.85, 95: 0.80, 90: 0.75, 85: 0.45, 80: 0.45, 75: 0.45, 70: 0.45, 65: 0.45 },
  },
};

const TRANSAMERICA = {
  type: 'life',
  productLine: 'Multi-product (FE / WL / IUL / Term)',
  products: {
    'FEX Immediate Solution': { 145: 1.25, 140: 1.25, 135: 1.20, 130: 1.15, 125: 1.10, 120: 1.05, 115: 1.00, 110: 0.95, 105: 0.90, 100: 0.85, 95: 0.80, 90: 0.75, 85: 0.70, 80: 0.65, 75: 0.60, 70: 0.55, 65: 0.55 },
    'Whole Life':             { 145: 1.40, 140: 1.40, 135: 1.35, 130: 1.25, 125: 1.15, 120: 1.10, 115: 1.05, 110: 1.00, 105: 0.95, 100: 0.90, 95: 0.85, 90: 0.80, 85: 0.75, 80: 0.70, 75: 0.65, 70: 0.60, 65: 0.60 },
    'FFIUL':                  { 145: 1.22, 140: 1.22, 135: 1.17, 130: 1.12, 125: 1.07, 120: 1.02, 115: 0.97, 110: 0.92, 105: 0.87, 100: 0.82, 95: 0.77, 90: 0.72, 85: 0.67, 80: 0.62, 75: 0.57, 70: 0.52, 65: 0.52 },
    'Term Super 20/25/30':    { 145: 1.05, 140: 1.05, 135: 1.00, 130: 0.95, 125: 0.92, 120: 0.90, 115: 0.85, 110: 0.82, 105: 0.80, 100: 0.75, 95: 0.70, 90: 0.65, 85: 0.60, 80: 0.55, 75: 0.50, 70: 0.45, 65: 0.45 },
    'Graded FE Express':      { 145: 0.98, 140: 0.98, 135: 0.93, 130: 0.88, 125: 0.83, 120: 0.83, 115: 0.73, 110: 0.68, 105: 0.63, 100: 0.63, 95: 0.53, 90: 0.48, 85: 0.43, 80: 0.38, 75: 0.33, 70: 0.28, 65: 0.28 },
  },
};

// ────────────────────────────────────────────────────────────────────
// Annuity carriers — use named producer levels instead of FFL numbers.
// Rates are on the annuity DEPOSIT, not annual premium. The calculator
// treats the input as deposit amount for these carriers.
// ────────────────────────────────────────────────────────────────────

const FFL_ANNUITIES = {
  type: 'annuity',
  productLine: 'Fixed Annuities',
  products: {
    'Athene Perf Elite 10':         { green: 0.0525, yellow: 0.0600, blue: 0.0625, silver: 0.0650, gold: 0.0675, platinum: 0.0700, black: 0.0750, royal: 0.0800, red: 0.0850 },
    'NLG Zenith Growth 10':         { green: 0.0450, yellow: 0.0500, blue: 0.0600, silver: 0.0625, gold: 0.0650, platinum: 0.0675, black: 0.0700, royal: 0.0750, red: 0.0800 },
    'Forethought Choice Accum 10':  { green: 0.0500, yellow: 0.0500, blue: 0.0600, silver: 0.0600, gold: 0.0650, platinum: 0.0700, black: 0.0750, royal: 0.0850, red: 0.0850 },
    'N. American Charter Plus':     { green: 0.0560, yellow: 0.0595, blue: 0.0630, silver: 0.0665, gold: 0.0700, platinum: 0.0725, black: 0.0750, royal: 0.0800, red: 0.0900 },
    'SILAC Denali 14':              { green: 0.0650, yellow: 0.0675, blue: 0.0700, silver: 0.0725, gold: 0.0750, platinum: 0.0775, black: 0.0800, royal: 0.0825, red: 0.0850 },
    'F&G Accumulator Plus 10':      { green: 0.0550, yellow: 0.0600, blue: 0.0650, silver: 0.0700, gold: 0.0750, platinum: 0.0800, black: 0.0850, royal: 0.0900, red: 0.0900 },
  },
};

// ────────────────────────────────────────────────────────────────────
// Carrier registry + lookup
// ────────────────────────────────────────────────────────────────────

export const FFL_COMMISSION_RATES = {
  'Aetna':              AETNA,
  'Aflac':              AFLAC,
  'Americo':            AMERICO,
  'American Amicable':  AMERICAN_AMICABLE,
  'Corebridge':         COREBRIDGE,
  'Ethos':              ETHOS,
  'Foresters':          FORESTERS,
  'InstaBrain':         INSTABRAIN,
  'Mutual of Omaha':    MUTUAL_OF_OMAHA,
  'NLG':                NLG_LIFE,
  'Royal Neighbors':    ROYAL_NEIGHBORS,
  'Transamerica':       TRANSAMERICA,
  'FFL Annuities':      FFL_ANNUITIES,
};

export const FFL_CARRIERS = Object.keys(FFL_COMMISSION_RATES);

// ────────────────────────────────────────────────────────────────────
// Pay breakdown calculator
//
// Returns null if any input is missing/invalid. Otherwise:
//   {
//     rate,              // decimal (1.25 = 125%)
//     ratePct,           // formatted "125%"
//     annualPremium,     // annual AP in $
//     annualCommission,  // AP × rate
//     advance9Month,     // 75% of annualCommission, paid upfront
//     advanceMonthly,    // advance9Month / 9 — what you'd see per month
//     earnedRemaining,   // 25% of annualCommission, paid as earned
//     earnedPerMonth,    // earnedRemaining / 3 — months 10/11/12
//     chargebackFloor,   // max chargeback exposure if lapse in month 1
//   }
// ────────────────────────────────────────────────────────────────────

export function calcCommission({ carrier, product, level, annualPremium }) {
  const c = FFL_COMMISSION_RATES[carrier];
  if (!c) return null;
  const rates = c.products?.[product];
  if (!rates) return null;
  const rate = rates[level];
  if (rate == null || rate === undefined) return null;
  const ap = Number(annualPremium);
  if (!Number.isFinite(ap) || ap <= 0) return null;

  const annualCommission = ap * rate;
  const advance9Month    = annualCommission * 0.75;
  const advanceMonthly   = advance9Month / 9;
  const earnedRemaining  = annualCommission * 0.25;
  const earnedPerMonth   = earnedRemaining / 3;

  return {
    rate,
    ratePct: formatPct(rate),
    annualPremium: ap,
    annualCommission,
    advance9Month,
    advanceMonthly,
    earnedRemaining,
    earnedPerMonth,
    chargebackFloor: advance9Month,
  };
}

// Format helpers exported so the view can use the same rounding rules
// the engine uses internally.
export function fmtMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0.00';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatPct(decimal) {
  if (!Number.isFinite(decimal)) return '—';
  const pct = decimal * 100;
  return (pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)) + '%';
}

// Sanitize a digit-string input. Allows one decimal point, strips
// everything else. Use this on every keystroke instead of `type="number"`
// which fights React's controlled-input model on partial entries.
export function sanitizeMoneyInput(raw) {
  if (raw == null) return '';
  const s = String(raw).replace(/[^0-9.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot === -1) return s;
  // Keep first dot, drop the rest.
  return s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
}
